import { Grid } from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Loader } from 'common/components/Loader/Loader';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { SORT_ORDER } from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	ReportingDashboardType,
	ReportingLibraryTabs,
} from 'common/constants/ReportingLibraryConstants';
import {
	APP_GENERAL_ERROR_MSG,
	APP_GENERIC_BAD_REQUEST_MSG,
} from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';

import { getReports } from './api';
import { IReportingCardData } from './api/type-def';
import ReportingCard from './Cards/ReportingCard';
import ReportingPagination from './ReportingPagination';

import styles from './ReportingLibrary.module.scss';

const { OK, BAD_REQUEST, NOT_FOUND } = HTTP_STATUS_CODES;

const {
	RECENTLY_VIEWED_TAB,
	FAVORITES_TAB,
	REQUIREMENT_INVENTORY_TAB,
	HORIZON_SCANNING_TAB,
	PRCI_TAB,
	MANAGEMENT_TAB,
	BOARD_TAB,
} = ReportingLibraryTabs;
const {
	RECENTLY_VIEWED,
	FAVORITE,
	REGULATION_INVENTORY,
	HORIZON_SCANNING,
	PRCI,
	MANAGEMENT,
	BOARD,
} = ReportingDashboardType;

const { ASCENDING, DESCENDING } = SORT_ORDER;

const ReportingLibraryCardView = () => {
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [reports, setReports] = useState<IReportingCardData[] | null>(null);
	const [totalPagesCount, setTotalPagesCount] = useState<number>(0);

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const activeReportingTabName = useSelector(
		(state: RootState) =>
			state?.common?.selectedReportingLibraryTab.reportingLibraryTabName,
	);
	const loggedInUser = useSelector(
		(state: RootState) => state?.common?.loggedInUser,
	);

	const defaultTableQueryParams = {
		pageNumber: 1,
		pageSize: 10,
		sortKey: 'lastAccessedByUser',
		sortDirection: DESCENDING,
		dashboardType: RECENTLY_VIEWED,
		reportType: '',
		userId: loggedInUser?.userId,
	};
	const getRequestParams = (data: any) => {
		const queryParams = { ...defaultTableQueryParams, ...data };
		switch (activeReportingTabName) {
			case RECENTLY_VIEWED_TAB:
				return queryParams;
			case FAVORITES_TAB:
				return {
					...queryParams,
					dashboardType: FAVORITE,
					sortKey: 'reportName',
					sortDirectiion: ASCENDING,
				};
			case REQUIREMENT_INVENTORY_TAB:
				return {
					...queryParams,
					dashboardType: REGULATION_INVENTORY,
					reportType: REGULATION_INVENTORY,
					sortKey: 'reportName',
					sortDirectiion: ASCENDING,
				};
			case HORIZON_SCANNING_TAB:
				return {
					...queryParams,
					dashboardType: HORIZON_SCANNING,
					reportType: HORIZON_SCANNING,
					sortKey: 'reportName',
					sortDirectiion: ASCENDING,
				};
			case PRCI_TAB:
				return {
					...queryParams,
					dashboardType: PRCI,
					reportType: PRCI,
					sortKey: 'reportName',
					sortDirectiion: ASCENDING,
				};
			case MANAGEMENT_TAB:
				return {
					...queryParams,
					dashboardType: MANAGEMENT,
					reportType: MANAGEMENT,
					sortKey: 'reportName',
					sortDirectiion: ASCENDING,
				};
			case BOARD_TAB:
				return {
					...queryParams,
					dashboardType: BOARD,
					reportType: BOARD,
					sortKey: 'reportName',
					sortDirectiion: ASCENDING,
				};
			default:
				return null;
		}
	};

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);

	const handleError = (statusCode: number) => {
		switch (statusCode) {
			case BAD_REQUEST:
				dispatch(
					setToastMessage({
						message: APP_GENERIC_BAD_REQUEST_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
				break;
			case NOT_FOUND:
				setReports([]);
				break;
			default:
				dispatch(
					setToastMessage({
						message: APP_GENERAL_ERROR_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
				break;
		}
	};

	const fetchReportsData = async (data: any) => {
		try {
			const response = await getReports(getRequestParams(data));
			if (response?.statusCode === OK) {
				setReports(response.responseBody?.content ?? []);
				setTotalPagesCount(response.responseBody?.totalPages ?? 1);
			} else {
				handleError(response.statusCode);
			}
			setIsLoading(false);
		} catch (ex) {
			dispatch(
				setToastMessage({
					message: APP_GENERAL_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	const handlePageChange = (data: any) => {
		fetchReportsData(data);
	};

	const getCardView = () => (
		<div>
			<ReportingCard
				reportingCards={reports}
				fetchReportsData={fetchReportsData}
			/>
			{totalPagesCount !== 0 && (
				<ReportingPagination
					defaultTableQueryParams={defaultTableQueryParams}
					totalPagesCount={totalPagesCount}
					handlePageChange={handlePageChange}
				/>
			)}
		</div>
	);

	useEffect(() => {
		defaultTableQueryParams.pageNumber -= 1;
		fetchReportsData(defaultTableQueryParams);
	}, [activeReportingTabName]);

	return (
		<Grid
			className={styles['reporting-card-layout']}
			data-testid="reporting-card-view"
		>
			{isLoading && <Loader show />}
			{!isLoading && getCardView()}
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</Grid>
	);
};

export default ReportingLibraryCardView;
